import { useState } from "react";

import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaInfoCircle } from "react-icons/fa";

import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { ResumeIndicatorB6ByHealthTeamData } from "@/services/esus/indicator/esb/b6/getResumeIndicatorB6ByHealthTeam";
import {
	getIndicatorClassification,
	getIndicatorClassificationColorClassName,
	getIndicatorClassificationScores,
	IndicatorEsbEnum,
} from "@/utils/indicatorsEsb";
import { calculatePercentage } from "@/utils/calculatePercentage";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEsbB6HealthTeamsClassificationTableColumns = {
	mainHealthTeamName: Column;
	linkedHealthTeamName: Column;
	totalAtraumaticRestorativeTreatmentProceduresLinked: Column;
	totalAtraumaticRestorativeTreatmentProceduresNotLinked: Column;
	totalProceduresLinked: Column;
	totalProceduresNotLinked: Column;
	classificationPercentageLinked: Column;
	classificationLinked: Column;
	classificationPercentage: Column;
	classification: Column;
};

type IndicatorEsbB6HealthTeamsTableProps = {
	healthTeamsClassifications: ResumeIndicatorB6ByHealthTeamData[];
	isLoading: boolean;
};

const indicatorClassificationScores = getIndicatorClassificationScores(IndicatorEsbEnum.B6);

export function IndicatorEsbB6HealthTeamsClassificationTable({
	healthTeamsClassifications,
	isLoading,
}: Readonly<IndicatorEsbB6HealthTeamsTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsbB6HealthTeamsClassificationTableColumns>({
		mainHealthTeamName: { title: "Equipe ESB", visible: true },
		linkedHealthTeamName: { title: "Equipe ESF", visible: true },
		totalAtraumaticRestorativeTreatmentProceduresLinked: {
			title: "Procedimentos TRA em cidadãos vinculados",
			visible: true,
		},
		totalAtraumaticRestorativeTreatmentProceduresNotLinked: {
			title: "Procedimentos TRA em cidadãos não vinculados",
			visible: true,
		},
		totalProceduresLinked: {
			title: "Procedimentos restauradores em cidadãos vinculados",
			visible: true,
		},
		totalProceduresNotLinked: {
			title: "Procedimentos restauradores em cidadãos não vinculados",
			visible: true,
		},
		classificationPercentageLinked: {
			title: "% Classificação cidadãos vinculados",
			visible: false,
		},
		classificationLinked: { title: "Classificação cidadãos vinculados", visible: false },
		classificationPercentage: { title: "% Classificação", visible: true },
		classification: { title: "Classificação", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th
						className={`text-start px-2 ${
							!columns.mainHealthTeamName.visible && "d-none"
						}`}
					>
						{columns.mainHealthTeamName.title}
					</th>
					<th
						className={`text-start px-2 ${
							!columns.linkedHealthTeamName.visible && "d-none"
						}`}
					>
						{columns.linkedHealthTeamName.title}
					</th>
					<th
						className={`px-2 ${
							!columns.totalAtraumaticRestorativeTreatmentProceduresLinked.visible &&
							"d-none"
						}`}
					>
						{columns.totalAtraumaticRestorativeTreatmentProceduresLinked.title}{" "}
						<CardOverlayTrigger
							header="Total de Procedimentos TRA em cidadãos vinculados"
							icon={<FaInfoCircle />}
						>
							<div className="text-start">
								Quantidade de procedimentos de tratamento restaurador atraumático
								(TRA) realizados no período <br />
								em cidadãos vinculados a equipes ESF que possuem vínculo com a
								equipe ESB do atendimento
							</div>
						</CardOverlayTrigger>
					</th>
					<th
						className={`px-2 ${
							!columns.totalAtraumaticRestorativeTreatmentProceduresNotLinked
								.visible && "d-none"
						}`}
					>
						{columns.totalAtraumaticRestorativeTreatmentProceduresNotLinked.title}{" "}
						<CardOverlayTrigger
							header="Total de Procedimentos TRA em cidadãos não vinculados"
							icon={<FaInfoCircle />}
						>
							<div className="text-start">
								Quantidade de procedimentos de tratamento restaurador atraumático
								(TRA) realizados no período <br />
								em cidadãos vinculados a equipes ESF que não possuem vínculo com a
								equipe ESB do atendimento
							</div>
						</CardOverlayTrigger>
					</th>
					<th className={`px-2 ${!columns.totalProceduresLinked.visible && "d-none"}`}>
						{columns.totalProceduresLinked.title}{" "}
						<CardOverlayTrigger
							header="Total de procedimentos restauradores em cidadãos vinculados"
							icon={<FaInfoCircle />}
						>
							<div className="text-start">
								Quantidade de procedimentos restauradores realizados no período
								em cidadãos vinculados <br />a equipes ESF que possuem vínculo com a
								equipe ESB do atendimento
							</div>
						</CardOverlayTrigger>
					</th>
					<th className={`px-2 ${!columns.totalProceduresNotLinked.visible && "d-none"}`}>
						{columns.totalProceduresNotLinked.title}{" "}
						<CardOverlayTrigger
							header="Total de procedimentos restauradores em cidadãos não vinculados"
							icon={<FaInfoCircle />}
						>
							<div className="text-start">
								Quantidade de procedimentos restauradores realizados no período
								em cidadãos vinculados <br />a equipes ESF que não possuem vínculo
								com a equipe ESB do atendimento
							</div>
						</CardOverlayTrigger>
					</th>
					<th className={`px-2 ${!columns.classificationPercentageLinked.visible && "d-none"}`}>
						{columns.classificationPercentageLinked.title}{" "}
						<CardOverlayTrigger header={"Classificação"} icon={<FaInfoCircle />}>
							<div className="text-start">
								Percentual de procedimentos TRA em cidadãos vinculados sobre a quantidade <br />
								de procedimentos restauradores em cidadãos vinculados
							</div>
						</CardOverlayTrigger>
					</th>
					<th className={`px-2 ${!columns.classificationLinked.visible && "d-none"}`}>
						{columns.classificationLinked.title}{" "}
						<CardOverlayTrigger header={"Classificação"} icon={<FaInfoCircle />}>
							<div className="text-start">
								<div className="text-primary">{`Ótimo: > ${indicatorClassificationScores.awesome}%`}</div>
								<br />
								<div className="text-success">{`Bom: > ${indicatorClassificationScores.good}% e <= ${indicatorClassificationScores.awesome}%`}</div>
								<br />
								<div className="text-warning">
									{`Suficiente: > ${indicatorClassificationScores.enough}% e <= ${indicatorClassificationScores.good}%`}
								</div>
								<br />
								<div className="text-danger">{`Regular: <= ${indicatorClassificationScores.enough}%`}</div>
								<br />
							</div>
						</CardOverlayTrigger>
					</th>
					<th className={`px-2 ${!columns.classificationPercentage.visible && "d-none"}`}>
						{columns.classificationPercentage.title}{" "}
						<CardOverlayTrigger header={"Classificação"} icon={<FaInfoCircle />}>
							<div className="text-start">
								Percentual de procedimentos TRA em cidadãos vinculados e não vinculados sobre <br />
								a quantidade de procedimentos restauradores em cidadãos vinculados e não vinculados
							</div>
						</CardOverlayTrigger>
					</th>
					<th className={`px-2 ${!columns.classification.visible && "d-none"}`}>
						{columns.classification.title}{" "}
						<CardOverlayTrigger header={"Classificação"} icon={<FaInfoCircle />}>
							<div className="text-start">
								<div className="text-primary">{`Ótimo: > ${indicatorClassificationScores.awesome}%`}</div>
								<br />
								<div className="text-success">{`Bom: > ${indicatorClassificationScores.good}% e <= ${indicatorClassificationScores.awesome}%`}</div>
								<br />
								<div className="text-warning">
									{`Suficiente: > ${indicatorClassificationScores.enough}% e <= ${indicatorClassificationScores.good}%`}
								</div>
								<br />
								<div className="text-danger">{`Regular: <= ${indicatorClassificationScores.enough}%`}</div>
								<br />
							</div>
						</CardOverlayTrigger>
					</th>
					<th className="text-nowrap pb-0">
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsbB6HealthTeamsClassificationTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsbB6HealthTeamsClassificationTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsbB6HealthTeamsClassificationTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && healthTeamsClassifications.length > 0
					? healthTeamsClassifications.map((healthTeamClassification, index) => (
							<tr
								className="text-center"
								key={`health-team-classification-table-row-${index + 1}`}
							>
								<td
									className={`text-start px-2 ${
										!columns.mainHealthTeamName.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>{healthTeamClassification.esbHealthTeamName}</div>
										<div className="text-muted">
											{healthTeamClassification.esbHealthTeamIne}
										</div>
									</div>
								</td>
								<td
									className={`text-start px-2 ${
										!columns.linkedHealthTeamName.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>{healthTeamClassification.linkedHealthTeamName}</div>
										<div className="text-muted">
											{healthTeamClassification.linkedHealthTeamIne}
										</div>
									</div>
								</td>
								<td
									className={`px-2 ${
										!columns.totalAtraumaticRestorativeTreatmentProceduresLinked
											.visible && "d-none"
									}`}
								>
									{healthTeamClassification.totalAtraumaticRestorativeTreatmentProceduresLinked ??
										0}
								</td>
								<td
									className={`px-2 ${
										!columns
											.totalAtraumaticRestorativeTreatmentProceduresNotLinked
											.visible && "d-none"
									}`}
								>
									{(healthTeamClassification.totalAtraumaticRestorativeTreatmentProcedures ??
										0) -
										(healthTeamClassification.totalAtraumaticRestorativeTreatmentProceduresLinked ??
											0)}
								</td>
								<td
									className={`px-2 ${
										!columns.totalProceduresLinked.visible && "d-none"
									}`}
								>
									{healthTeamClassification.totalProceduresLinked ?? 0}
								</td>
								<td
									className={`px-2 ${
										!columns.totalProceduresNotLinked.visible && "d-none"
									}`}
								>
									{(healthTeamClassification.totalProcedures ?? 0) -
										(healthTeamClassification.totalProceduresLinked ?? 0)}
								</td>
								<td
									className={`px-2 ${
										!columns.classificationPercentageLinked.visible && "d-none"
									}`}
								>
									{`${calculatePercentage(
										healthTeamClassification.totalAtraumaticRestorativeTreatmentProceduresLinked,
										healthTeamClassification.totalProceduresLinked
									)}%`}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.classificationLinked.visible && "d-none"
									}`}
								>
									<Badge
										bg={getIndicatorClassificationColorClassName(
											IndicatorEsbEnum.B6,
											calculatePercentage(
												healthTeamClassification.totalAtraumaticRestorativeTreatmentProceduresLinked,
												healthTeamClassification.totalProceduresLinked
											)
										)}
									>
										{getIndicatorClassification(
											IndicatorEsbEnum.B6,
											calculatePercentage(
												healthTeamClassification.totalAtraumaticRestorativeTreatmentProceduresLinked,
												healthTeamClassification.totalProceduresLinked
											)
										)}
									</Badge>
								</td>
								<td
									className={`px-2 ${
										!columns.classificationPercentage.visible && "d-none"
									}`}
								>
									{`${calculatePercentage(
										healthTeamClassification.totalAtraumaticRestorativeTreatmentProcedures,
										healthTeamClassification.totalProcedures
									)}%`}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.classification.visible && "d-none"
									}`}
								>
									<Badge
										bg={getIndicatorClassificationColorClassName(
											IndicatorEsbEnum.B6,
											calculatePercentage(
												healthTeamClassification.totalAtraumaticRestorativeTreatmentProcedures,
												healthTeamClassification.totalProcedures
											)
										)}
									>
										{getIndicatorClassification(
											IndicatorEsbEnum.B6,
											calculatePercentage(
												healthTeamClassification.totalAtraumaticRestorativeTreatmentProcedures,
												healthTeamClassification.totalProcedures
											)
										)}
									</Badge>
								</td>
								<td></td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
